import React, { useEffect, useMemo, useState } from 'react';
import { LuArrowRight, LuCheck, LuChevronDown, LuShieldCheck } from 'react-icons/lu';
import { apiJson } from '../apiClient';
import { saveSubscriptionCheckout } from '../utils/subscriptionCheckout';
import { normalizeRole } from '../utils/userLocale';
import './SubscriptionPage.css';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-TZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const YEARLY_DISCOUNT_RATE = 0.2;

function formatTzs(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `TZS ${n.toLocaleString('en-TZ')}`;
}

function yearlyDiscountedMonthly(monthlyTzs) {
  return Math.round(monthlyTzs * (1 - YEARLY_DISCOUNT_RATE));
}

const SUBSCRIPTION_FAQ = [
  {
    id: 'trial',
    q: 'How long is the free trial?',
    a: 'New businesses get a full-feature trial with all modules unlocked. When it ends, pick a plan here to continue without losing your data.',
  },
  {
    id: 'data',
    q: 'Is my data safe?',
    a: 'Your sales, stock, and reports stay on secure servers with backups. Only users you invite can access your tenant.',
  },
  {
    id: 'payment',
    q: 'How do I pay for a plan?',
    a: 'Choose a plan and complete checkout with mobile money or bank transfer. We confirm payment and activate your subscription.',
  },
  {
    id: 'branches',
    q: 'Can I use multiple branches?',
    a: 'Higher plans support multiple branches and staff accounts. Contact us if you need Enterprise setup for many locations.',
  },
  {
    id: 'change-plan',
    q: 'Can I change plans later?',
    a: 'Yes. Select a new plan anytime. Upgrades apply on your next billing period unless support arranges an immediate switch.',
  },
  {
    id: 'expired',
    q: 'What if my subscription expires?',
    a: 'You can still sign in, but daily use is paused until you renew. Your records remain saved.',
  },
  {
    id: 'swahili',
    q: 'Do you support Swahili?',
    a: 'Our team helps in Swahili and English by phone, WhatsApp, and email.',
  },
];

const SubscriptionPage = ({ user, onNavigate }) => {
  const sub = user?.subscription || {};
  const tenant = user?.tenant || {};
  const isOwner = normalizeRole(user?.role) === 'owner';
  const businessType = tenant.businessType || '';

  const [billing, setBilling] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [openFaqId, setOpenFaqId] = useState(SUBSCRIPTION_FAQ[0]?.id || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiJson('/owner/subscription-billing');
        if (!cancelled) {
          setBilling(data);
          setLoadErr('');
        }
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || 'Could not load billing.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  const liveSub = billing?.subscription || sub;
  const status = liveSub.status || 'trial';
  const currentPlanCode = liveSub.planCode || sub.planCode;
  const allows = liveSub.allowsAccess !== false;
  const daysRemaining = liveSub.daysRemaining ?? liveSub.daysLeftInTrial ?? 0;
  const onTrial = status === 'trial' && allows;
  const trialEndsAt = liveSub.trialEndsAt || sub.trialEndsAt;
  const payments = billing?.payments || [];
  const plans = billing?.plans || [];

  const sortedPlans = useMemo(() => {
    const list = [...plans];
    list.sort((a, b) => (a.priceTzs || 0) - (b.priceTzs || 0));
    return list;
  }, [plans]);

  const displayPrice = (plan) => {
    if (plan.isCustomPricing) return null;
    const monthly = plan.priceTzs || 0;
    if (billingPeriod === 'yearly') {
      return yearlyDiscountedMonthly(monthly);
    }
    return monthly;
  };

  const isPopularPlan = (plan) => !plan.isCustomPricing && !!plan.isPopular;

  const isRecommendedPlan = (plan) =>
    plan.recommendedFor === businessType && !plan.isPopular && !plan.isCustomPricing;

  const handleSelectPlan = (plan) => {
    if (plan.isCustomPricing) {
      if (typeof onNavigate === 'function') onNavigate('support');
      return;
    }
    saveSubscriptionCheckout({
      ...plan,
      priceTzs: displayPrice(plan),
      billingPeriod,
    });
    onNavigate('subscription-checkout');
  };

  const ctaLabel = (plan) => {
    if (plan.isCustomPricing) return 'Contact sales';
    if (onTrial) return 'Start free trial';
    return 'Continue to payment';
  };

  return (
    <div className="page-bleed subscription-page">
      <div className="subscription-pricing-band">
      <section className="subscription-pricing-hero">
        <span className="subscription-pricing-hero__eyebrow">Pricing</span>
        <h1 className="subscription-pricing-hero__title">Simple, transparent pricing</h1>
        <p className="subscription-pricing-hero__lead">
          Affordable plans in Tanzanian Shillings. Start free, scale as you grow.
        </p>

        <div className="subscription-billing-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`subscription-billing-toggle__btn${billingPeriod === 'monthly' ? ' subscription-billing-toggle__btn--active' : ''}`}
            onClick={() => setBillingPeriod('monthly')}
            aria-pressed={billingPeriod === 'monthly'}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`subscription-billing-toggle__btn${billingPeriod === 'yearly' ? ' subscription-billing-toggle__btn--active' : ''}`}
            onClick={() => setBillingPeriod('yearly')}
            aria-pressed={billingPeriod === 'yearly'}
          >
            Yearly
            <span className="subscription-billing-toggle__save">−20%</span>
          </button>
        </div>
        {billingPeriod === 'yearly' ? (
          <p className="subscription-pricing-hero__discount-note">
            Pay yearly and save {Math.round(YEARLY_DISCOUNT_RATE * 100)}% — prices below are per month on annual billing.
          </p>
        ) : null}
      </section>

      {!allows ? (
        <div className="subscription-page__warn subscription-page__warn--center" role="alert">
          <strong>Access suspended</strong> — your trial or payment period has ended. Choose a plan below or contact
          support.
        </div>
      ) : null}

      {status === 'active' ? (
        <div className="subscription-active-banner subscription-active-banner--center">
          <strong>Active subscription</strong> — {liveSub.planName || 'your plan'}
          {liveSub.currentPeriodEnd ? <> until {formatDate(liveSub.currentPeriodEnd)}</> : null}
        </div>
      ) : null}

      {onTrial ? (
        <div className="subscription-trial-strip" role="status">
          <LuShieldCheck size={20} aria-hidden />
          <span>
            <strong>Free trial</strong>
            {daysRemaining > 0 ? (
              <>
                {' '}
                — {daysRemaining} days left (ends {formatDate(trialEndsAt)}). All features unlocked.
              </>
            ) : (
              <> — ends {formatDate(trialEndsAt)}. Pick a plan to continue.</>
            )}
          </span>
        </div>
      ) : null}

      <section
        id="pricing"
        className="subscription-pricing-section"
        aria-labelledby="pricing-plans-heading"
      >
        <h2 id="pricing-plans-heading" className="visually-hidden">
          Subscription plans
        </h2>

        {loading ? (
          <div className="subscription-pricing-grid subscription-pricing-grid--loading" aria-busy="true">
            <div className="subscription-pricing-card subscription-pricing-card--skeleton" />
            <div className="subscription-pricing-card subscription-pricing-card--skeleton" />
            <div className="subscription-pricing-card subscription-pricing-card--skeleton" />
            <div className="subscription-pricing-card subscription-pricing-card--skeleton" />
          </div>
        ) : null}

        {loadErr ? <p className="subscription-page__note subscription-page__note--err subscription-page__note--center">{loadErr}</p> : null}

        {!loading && !loadErr ? (
          <div className="subscription-pricing-grid">
            {sortedPlans.map((plan) => {
              const isCurrent = currentPlanCode === plan.code;
              const popular = isPopularPlan(plan);
              const recommended = isRecommendedPlan(plan);
              const price = displayPrice(plan);

              return (
                <article
                  key={plan.code}
                  className={`subscription-pricing-card${popular ? ' subscription-pricing-card--popular' : ''}${
                    isCurrent ? ' subscription-pricing-card--current' : ''
                  }${plan.isCustomPricing ? ' subscription-pricing-card--enterprise' : ''}`}
                >
                  {popular ? (
                    <span className="subscription-pricing-card__ribbon">Most popular</span>
                  ) : null}
                  {isCurrent ? (
                    <span className="subscription-pricing-card__badge">Your plan</span>
                  ) : recommended && !popular ? (
                    <span className="subscription-pricing-card__badge subscription-pricing-card__badge--muted">
                      Best for you
                    </span>
                  ) : null}

                  <h3 className="subscription-pricing-card__name">{plan.name}</h3>
                  <p className="subscription-pricing-card__tagline">{plan.tagline}</p>

                  <div className="subscription-pricing-card__price-block">
                    {plan.isCustomPricing ? (
                      <p className="subscription-pricing-card__price subscription-pricing-card__price--custom">
                        Custom
                      </p>
                    ) : (
                      <>
                        {billingPeriod === 'yearly' ? (
                          <p className="subscription-pricing-card__was" aria-label="Regular monthly price">
                            {formatTzs(plan.priceTzs)}
                            <span className="subscription-pricing-card__period">/ month</span>
                          </p>
                        ) : null}
                        <p className="subscription-pricing-card__price">
                          {formatTzs(price)}
                          <span className="subscription-pricing-card__period">/ month</span>
                        </p>
                      </>
                    )}
                    {billingPeriod === 'yearly' && !plan.isCustomPricing ? (
                      <p className="subscription-pricing-card__billed">
                        Billed yearly — {formatTzs(price * 12)} total
                      </p>
                    ) : null}
                  </div>

                  <ul className="subscription-pricing-card__features">
                    {(plan.highlights || []).map((h) => (
                      <li key={h}>
                        <LuCheck className="subscription-pricing-card__check" aria-hidden />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  {!isCurrent ? (
                    <button
                      type="button"
                      className={`subscription-pricing-card__cta${
                        popular || recommended ? ' subscription-pricing-card__cta--primary' : ''
                      }`}
                      onClick={() => handleSelectPlan(plan)}
                    >
                      {ctaLabel(plan)}
                      {!plan.isCustomPricing ? <LuArrowRight size={18} aria-hidden /> : null}
                    </button>
                  ) : (
                    <p className="subscription-pricing-card__current">Current plan</p>
                  )}
                </article>
              );
            })}
          </div>
        ) : null}

        <p className="subscription-pricing-footnote">
          Prices in TZS. Yearly billing saves {Math.round(YEARLY_DISCOUNT_RATE * 100)}% vs paying monthly. Mobile money
          checkout coming soon — we will confirm your plan after payment.
        </p>
      </section>
      </div>

      <section id="faq" className="subscription-faq-section" aria-labelledby="subscription-faq-heading">
        <h2 id="subscription-faq-heading" className="subscription-section-title subscription-section-title--center">
          Frequently asked questions
        </h2>
        <p className="subscription-faq-section__lead">
          Answers about trials, billing, branches, and support.
        </p>
        <div className="subscription-faq-list">
          {SUBSCRIPTION_FAQ.map((item) => {
            const open = openFaqId === item.id;
            return (
              <div key={item.id} className={`subscription-faq-item${open ? ' subscription-faq-item--open' : ''}`}>
                <button
                  type="button"
                  className="subscription-faq-item__trigger"
                  aria-expanded={open}
                  onClick={() => setOpenFaqId(open ? null : item.id)}
                >
                  <span>{item.q}</span>
                  <LuChevronDown className="subscription-faq-item__icon" aria-hidden />
                </button>
                {open ? <p className="subscription-faq-item__answer">{item.a}</p> : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="subscription-account-panel" id="account">
        <h2 className="subscription-section-title">Your account</h2>
        <div className="subscription-account-grid">
          <div className="subscription-account-card">
            <span className="subscription-account-card__label">Business</span>
            <strong>{tenant.name || '—'}</strong>
          </div>
          <div className="subscription-account-card">
            <span className="subscription-account-card__label">Status</span>
            <strong className={`subscription-status subscription-status--${status}`}>{status}</strong>
          </div>
          <div className="subscription-account-card">
            <span className="subscription-account-card__label">Current plan</span>
            <strong>{liveSub.planName || sub.planName || '—'}</strong>
          </div>
          <div className="subscription-account-card">
            <span className="subscription-account-card__label">Price / month</span>
            <strong>{formatTzs(liveSub.monthlyPriceTzs ?? sub.monthlyPriceTzs)}</strong>
          </div>
        </div>
      </section>

      <section className="subscription-payments-section">
        <div className="subscription-plan-box">
          <h2 className="subscription-plan-box__title">Payment history</h2>
          {!isOwner ? (
            <p className="subscription-page__note">Only the business owner can view payments.</p>
          ) : payments.length === 0 ? (
            <p className="subscription-page__note">No payments recorded yet.</p>
          ) : (
            <div className="subscription-payments-wrap">
              <table className="subscription-payments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td>{formatDate(p.paidAt)}</td>
                      <td>{formatTzs(p.amountTzs)}</td>
                      <td>{p.method || '—'}</td>
                      <td>{p.reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <footer className="subscription-footer">
        <button type="button" className="subscription-back-btn" onClick={() => onNavigate('overview')}>
          Back to dashboard
        </button>
      </footer>
    </div>
  );
};

export default SubscriptionPage;
